/**
 * « Signaler cette question » (migration 0348) : après avoir répondu, l'élève
 * signale une erreur ou propose une amélioration. Les admins et l'auteur de
 * l'exercice sont prévenus. Pour eux (admin / auteur qui teste son exercice),
 * un lien ouvre directement la question dans l'éditeur.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Pencil } from 'lucide-react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Button, cn, MarkdownMath } from '@jeraisonne/ui';
import { isAdmin, useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { Sheet } from '@/features/evaluations/sheet';
import {
  QUESTION_REPORT_KIND_LABELS,
  QUESTION_REPORT_MESSAGE_MAX,
  reportMessageIssue,
  useReportQuestion,
  type QuestionReportKind,
} from '@/lib/exercise-v2/question-reports';

const db = supabase as unknown as SupabaseClient;

/** Auteur de l'exercice ou admin : peut modifier directement. */
function useCanEditExercise(exerciseId: string) {
  const { user, profile } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const author = useQuery({
    queryKey: ['exercise-author', exerciseId],
    enabled: !admin && !!user,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await db
        .from('exercises')
        .select('created_by')
        .eq('id', exerciseId)
        .maybeSingle();
      return (data as { created_by: string | null } | null)?.created_by ?? null;
    },
  });
  return admin || (!!user && author.data === user.id);
}

export function ReportQuestionSheet({
  exerciseId,
  questionBlockId,
  questionPrompt,
  attemptId,
  onClose,
}: {
  exerciseId: string;
  questionBlockId: string;
  questionPrompt: string;
  attemptId?: string | null;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const canEdit = useCanEditExercise(exerciseId);
  const report = useReportQuestion();
  const [kind, setKind] = useState<QuestionReportKind>('error');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);
  const issue = reportMessageIssue(message);

  async function send() {
    setTouched(true);
    if (issue || !user) return;
    try {
      await report.mutateAsync({
        exerciseId,
        questionBlockId,
        attemptId,
        kind,
        message,
        userId: user.id,
      });
    } catch {
      /* affiché via report.error */
    }
  }

  if (report.isSuccess) {
    return (
      <Sheet
        title="Merci !"
        onClose={onClose}
        footer={
          <Button fullWidth onClick={onClose}>
            Fermer
          </Button>
        }
      >
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500" aria-hidden />
          <p className="text-ink-900 font-semibold">Ton signalement a bien été envoyé.</p>
          <p className="text-ink-500 text-sm">
            L’équipe JeRaisonne et l’auteur de l’exercice vont le regarder.
          </p>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      title="Signaler cette question"
      onClose={onClose}
      footer={
        <Button fullWidth onClick={send} disabled={report.isPending}>
          {report.isPending ? 'Envoi…' : 'Envoyer'}
        </Button>
      }
    >
      {questionPrompt && (
        <div className="bg-ink-50 ocean:bg-white/5 text-ink-700 rounded-xl px-3 py-2 text-sm">
          <MarkdownMath>{questionPrompt}</MarkdownMath>
        </div>
      )}

      <div
        role="radiogroup"
        aria-label="Type de signalement"
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {(Object.keys(QUESTION_REPORT_KIND_LABELS) as QuestionReportKind[]).map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={kind === k}
            onClick={() => setKind(k)}
            className={cn(
              'min-h-[2.75rem] rounded-xl border px-3 py-2 text-left text-sm font-semibold active:scale-[0.98]',
              kind === k
                ? 'border-primary-500 bg-primary-50 text-primary-700 ocean:bg-primary-500/15 ocean:text-primary-200'
                : 'border-ink-200 text-ink-700 bg-white',
            )}
          >
            {QUESTION_REPORT_KIND_LABELS[k]}
          </button>
        ))}
      </div>

      <label className="block">
        <span className="text-ink-700 mb-1 block text-sm font-semibold">
          {kind === 'error' ? 'Qu’est-ce qui ne va pas ?' : 'Que proposes-tu ?'}
        </span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onBlur={() => setTouched(true)}
          maxLength={QUESTION_REPORT_MESSAGE_MAX}
          rows={5}
          placeholder={
            kind === 'error'
              ? 'Ex. : la bonne réponse est 13, pas 12.'
              : 'Ex. : ajouter une figure, préciser l’unité…'
          }
          className="border-ink-200 text-ink-900 w-full rounded-xl border bg-white px-3 py-2 text-sm"
        />
      </label>
      {touched && issue && <p className="text-sm text-red-600">{issue}</p>}
      {report.isError && (
        <p role="alert" className="text-sm text-red-600">
          {report.error instanceof Error ? report.error.message : 'Erreur inconnue.'}
        </p>
      )}

      {canEdit && (
        <Link
          to="/tutor/exercises/$exerciseId/edit-v2"
          params={{ exerciseId }}
          search={{ question: questionBlockId }}
          className="text-primary-600 ocean:text-primary-300 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
        >
          <Pencil className="h-4 w-4" aria-hidden /> Modifier directement la question
        </Link>
      )}
    </Sheet>
  );
}
